/**
 * GuestContext — single source of truth for "is this session a guest?"
 * plus a globally-mounted SignupPromptModal opened via `promptSignup(...)`.
 * Any component can call `useGuest()` and either gate a feature (`isGuest`)
 * or open the trial CTA (`promptSignup({ headline, subhead })`).
 */
import React, { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "./AuthContext";
import SignupPromptModal from "../components/SignupPromptModal";

const GuestContext = createContext(null);

export function GuestProvider({ children }) {
  const { user } = useAuth();
  const isGuest = !user;
  const [prompt, setPrompt] = useState(null); // {headline, subhead} | null

  const promptSignup = useCallback((opts = {}) => {
    setPrompt({
      headline: opts.headline || "Sign up to unlock this",
      subhead:
        opts.subhead ||
        "Start your 14-day free All-Access trial. No card, cancel anytime.",
    });
  }, []);

  const closePrompt = useCallback(() => setPrompt(null), []);

  return (
    <GuestContext.Provider value={{ isGuest, promptSignup }}>
      {children}
      <SignupPromptModal
        open={!!prompt}
        onClose={closePrompt}
        headline={prompt?.headline}
        subhead={prompt?.subhead}
      />
    </GuestContext.Provider>
  );
}

export function useGuest() {
  const ctx = useContext(GuestContext);
  if (!ctx) {
    // Fallback for early tree calls — behave as signed-in
    return { isGuest: false, promptSignup: () => {} };
  }
  return ctx;
}
